import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import ComplianceGate from "./components/ComplianceGate";
import ErrorBoundary from "./components/ErrorBoundary";
import { DIRECTORY_ENABLED } from "./config/features";
import { Toaster } from "./components/ui/sonner";
import WhatsAppButton from "./components/WhatsAppButton";
import JaydeeMascot from "./components/JaydeeMascot";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import LandingPage from "./pages/LandingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import CustomersPage from "./pages/CustomersPage";
import DriversPage from "./pages/DriversPage";
import BusinessesPage from "./pages/BusinessesPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import InstallPage from "./pages/InstallPage";
import QuickPostPage from "./pages/QuickPostPage";
import QuickPostStatusPage from "./pages/QuickPostStatusPage";
import DirectoryPage from "./pages/DirectoryPage";
import DriverProfilePage from "./pages/DriverProfilePage";

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <div className="App">
          <BrowserRouter>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/for-customers" element={<CustomersPage />} />
            <Route path="/for-drivers" element={<DriversPage />} />
            <Route path="/for-businesses" element={<BusinessesPage />} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/app" element={<InstallPage />} />
            <Route path="/download" element={<InstallPage />} />
            <Route path="/quick-post" element={<QuickPostPage />} />
            <Route path="/quick-post/status/:guestToken" element={<QuickPostStatusPage />} />
            <Route path="/quick-post/status" element={<QuickPostStatusPage />} />
            {DIRECTORY_ENABLED ? (
              <>
                <Route path="/drivers" element={<DirectoryPage />} />
                <Route path="/drivers/:slug" element={<DriverProfilePage />} />
              </>
            ) : (
              <>
                <Route path="/drivers" element={<Navigate to="/" replace />} />
                <Route path="/drivers/:slug" element={<Navigate to="/" replace />} />
              </>
            )}

            {/* Protected Routes */}
            <Route
              path="/customer"
              element={
                <ProtectedRoute allowedRoles={["customer"]}>
                  <CustomerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver"
              element={
                <ProtectedRoute allowedRoles={["driver"]}>
                  <DriverComplianceGated />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <WhatsAppButton />
        <JaydeeMount />
      </div>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;

/**
 * Only mount Jaydee for the public marketing pages and customers.
 * Drivers and admins log in to do work — the marketing mascot would be
 * a distraction in their dashboards. `user === null` means the auth
 * check is still in-flight (leave him mounted); `user === false` means
 * signed-out (marketing / homepage → show).
 */
function JaydeeMount() {
  const { user } = useAuth();
  if (user && (user.role === 'driver' || user.role === 'admin')) return null;
  return <JaydeeMascot />;
}

function DriverComplianceGated() {
  const { user } = useAuth();
  return (
    <ComplianceGate user={user}>
      <DriverDashboard />
    </ComplianceGate>
  );
}
